import { useState } from 'react';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStoreActions } from '@/store/useStore';
import { CURRENT_ACADEMIC_TERM } from '@/config/academicTerm';
import {
  BG, ACCENT, ACCENT_BRIGHT, FONT_DISPLAY, FONT_BODY, FONT_MONO,
  TEXT, HAIRLINE, glow, microLabel, primaryButton,
} from '@/styles/premium';

interface SemesterMigrationNoticeProps {
  userName?: string;
}

export default function SemesterMigrationNotice({ userName }: SemesterMigrationNoticeProps) {
  const navigate = useNavigate();
  const { clearUserPreferences } = useStoreActions();

  const handleUpdateProfile = () => {
    // Clear old preferences
    clearUserPreferences();
    // Navigate to student form
    navigate('/student-form');
  };

  return (
    <div
      style={{
        background: BG,
        fontFamily: FONT_BODY,
        color: TEXT.primary,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px,5vw,48px)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ ...glow(420, 0.1), top: -140, right: -120 }} />
      <div style={{ ...glow(360, 0.07), bottom: -160, left: -140 }} />

      <div style={{ width: '100%', maxWidth: 560, position: 'relative', zIndex: 1 }}>
        <div
          style={{
            background: 'linear-gradient(135deg,rgba(20,184,166,.12) 0%,rgba(6,182,212,.07) 100%)',
            border: '1px solid rgba(20,184,166,.18)',
            borderRadius: 20,
            padding: 'clamp(24px,5vw,40px)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ ...glow(200, 0.16), top: -60, right: -40 }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Micro-label + icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(20px,4vw,28px)' }}>
              <div
                style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(20,184,166,.15)',
                  border: '1px solid rgba(20,184,166,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <RefreshCw size={19} color={ACCENT} aria-hidden />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...microLabel, marginBottom: 3 }}>Profile update needed</div>
                <div style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em', color: TEXT.ghost }}>
                  {CURRENT_ACADEMIC_TERM.toUpperCase()}
                </div>
              </div>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(30px,6.5vw,46px)',
                fontWeight: 400,
                letterSpacing: '-.03em',
                lineHeight: 1.02,
                marginBottom: 16,
              }}
            >
              {userName ? (
                <>Hi {userName},<br />
                  <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>new data is in</em>
                </>
              ) : (
                <>New semester<br />
                  <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>data is in</em>
                </>
              )}
            </h1>

            <p
              style={{
                fontSize: 'clamp(13px,1.6vw,14px)',
                lineHeight: 1.75,
                color: TEXT.muted,
                fontWeight: 300,
                marginBottom: 12,
                maxWidth: 440,
              }}
            >
              We've loaded the latest timetables for{' '}
              <span style={{ color: 'rgba(255,255,255,.7)' }}>{CURRENT_ACADEMIC_TERM}</span>.
            </p>
            <p
              style={{
                fontSize: 'clamp(13px,1.6vw,14px)',
                lineHeight: 1.75,
                color: TEXT.muted,
                fontWeight: 300,
                marginBottom: 'clamp(24px,4vw,32px)',
                maxWidth: 440,
              }}
            >
              Your saved timetable isn't in the current data, so pick your program again to get
              your new schedule.
            </p>

            <div style={{ borderTop: `1px solid ${HAIRLINE}`, paddingTop: 'clamp(20px,4vw,26px)' }}>
              <UpdateButton onClick={handleUpdateProfile} />
              <p
                style={{
                  fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em',
                  color: TEXT.ghost, marginTop: 12, textAlign: 'center', textTransform: 'uppercase',
                }}
              >
                Takes about a minute
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function UpdateButton({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...primaryButton,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: hov ? ACCENT_BRIGHT : ACCENT,
        borderColor: hov ? ACCENT_BRIGHT : ACCENT,
      }}
    >
      Update my profile <ArrowRight size={15} aria-hidden />
    </button>
  );
}
